import React, { useState, useEffect } from 'react';
import './PersonDetailModal.css';

const GeneralDetailModal = ({ general, onClose, onSave }) => {
  // Helper function to parse objectives from JSON string or return array
  const parseObjectives = (objectives) => {
    if (!objectives) return [];

    // If it's already an array, return it
    if (Array.isArray(objectives)) {
      return objectives;
    }

    // If it's a string, try to parse it
    if (typeof objectives === 'string') {
      try {
        const parsed = JSON.parse(objectives);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('Failed to parse objectives:', e);
        return [];
      }
    }

    return [];
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: general?.name || '',
    description: general?.description || '',
    status: general?.status || 'active',
    objectives: parseObjectives(general?.objectives),
    orderNumber: general?.orderNumber || 1
  });
  const [newObjective, setNewObjective] = useState('');

  useEffect(() => {
    if (general) {
      setEditData({
        name: general.name || '',
        description: general.description || '',
        status: general.status || 'active',
        objectives: parseObjectives(general.objectives),
        orderNumber: general.orderNumber || 1
      });
    }
  }, [general]);

  const handleSave = async () => {
    try {
      await onSave({ ...general, ...editData });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving general:', error);
      alert('Failed to save general details');
    }
  };

  const handleAddObjective = () => {
    if (newObjective.trim()) {
      setEditData({
        ...editData,
        objectives: [...editData.objectives, newObjective.trim()]
      });
      setNewObjective('');
    }
  };

  const handleRemoveObjective = (index) => {
    setEditData({
      ...editData,
      objectives: editData.objectives.filter((_, i) => i !== index)
    });
  };

  if (!general) return null;

  return (
    <div className="person-detail-modal-overlay" onClick={onClose}>
      <div
        className="person-detail-modal-wrapper"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '900px', maxHeight: '90vh' }}
      >
        <div className="person-detail-modal">
          {/* Header */}
          <div className="person-detail-header" style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
            padding: '24px',
            borderRadius: '12px 12px 0 0',
            color: 'white'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700' }}>
                  ⭐ {general.name}
                </h2>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '6px 14px',
                    borderRadius: '20px',
                    background: 'rgba(255, 255, 255, 0.25)',
                    fontSize: '13px',
                    fontWeight: '700',
                    border: '2px solid rgba(255, 255, 255, 0.4)'
                  }}>
                    GENERAL #{general.orderNumber}
                  </span>
                  <span style={{
                    padding: '6px 14px',
                    borderRadius: '20px',
                    background: general.status === 'active' ? '#10b981' : general.status === 'operational' ? '#3b82f6' : '#6b7280',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {general.status || 'active'}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="person-detail-body" style={{
            padding: '24px',
            maxHeight: 'calc(90vh - 200px)',
            overflowY: 'auto'
          }}>
            {/* Basic Information */}
            <div className="person-detail-section">
              <h3 className="person-detail-section-title">
                <span className="person-detail-section-icon">ℹ️</span>
                Basic Information
              </h3>
              <div className="person-detail-section-content">
                <div className="person-detail-field">
                  <label>General Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      className="person-detail-input"
                    />
                  ) : (
                    <p>{general.name}</p>
                  )}
                </div>

                <div className="person-detail-field">
                  <label>Description</label>
                  {isEditing ? (
                    <textarea
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      className="person-detail-textarea"
                      rows="4"
                    />
                  ) : (
                    <p>{general.description || 'No description provided'}</p>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="person-detail-field">
                    <label>Status</label>
                    {isEditing ? (
                      <select
                        value={editData.status}
                        onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                        className="person-detail-input"
                      >
                        <option value="active">Active</option>
                        <option value="operational">Operational</option>
                        <option value="planning">Planning</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    ) : (
                      <p style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        background: general.status === 'active' ? '#10b981' : general.status === 'operational' ? '#3b82f6' : '#6b7280',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {general.status || 'active'}
                      </p>
                    )}
                  </div>

                  <div className="person-detail-field">
                    <label>Order Number</label>
                    {isEditing ? (
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={editData.orderNumber}
                        onChange={(e) => setEditData({ ...editData, orderNumber: parseInt(e.target.value) })}
                        className="person-detail-input"
                      />
                    ) : (
                      <p>{general.orderNumber || 1}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Objectives */}
            <div className="person-detail-section">
              <h3 className="person-detail-section-title">
                <span className="person-detail-section-icon">🎯</span>
                Objectives
              </h3>
              <div className="person-detail-section-content">
                {isEditing && (
                  <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={newObjective}
                      onChange={(e) => setNewObjective(e.target.value)}
                      placeholder="Add new objective..."
                      className="person-detail-input"
                      style={{ flex: 1 }}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddObjective()}
                    />
                    <button
                      onClick={handleAddObjective}
                      className="person-detail-action-btn"
                      style={{ padding: '8px 16px' }}
                    >
                      Add
                    </button>
                  </div>
                )}

                {editData.objectives.length > 0 ? (
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {editData.objectives.map((objective, index) => (
                      <li key={index} style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        padding: '12px',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span>• {objective}</span>
                        {isEditing && (
                          <button
                            onClick={() => handleRemoveObjective(index)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.2)',
                              border: '1px solid rgba(239, 68, 68, 0.4)',
                              color: '#ef4444',
                              padding: '4px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                    No objectives set
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="person-detail-footer">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="person-detail-action-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="person-detail-action-btn"
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)'
                  }}
                >
                  💾 Save Changes
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="person-detail-action-btn"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)'
                }}
              >
                ✏️ Edit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralDetailModal;
